import { navigation } from '@shared-core/navigation'
import { sheetStore } from '@shared-core/navigation/sheet-store'
import { useHaptic } from '@shared-ui/platform/hooks/use-haptic'
import { hideArrowIndicator, updateArrowIndicator } from '@web/components/edge-swipe-back/arrow-indicator'
import styles from '@web/components/edge-swipe-back/styles.module.css'
import { getPhoneFramePortalElement } from '@web/components/phone-frame'
import { isTMA, isTMADesktop, isTMAFullscreen } from '@web/libs/tg/platform'
import { type PropsWithChildren, useEffect } from 'react'

const MOBILE_EDGE_WIDTH = 44
const DESKTOP_EDGE_WIDTH = 24
const LOCK_DISTANCE = 10
const MIN_BACK_DISTANCE = 56
const MIN_FAST_BACK_DISTANCE = 36
const FAST_SWIPE_VELOCITY = 0.25
const HORIZONTAL_DOMINANCE_RATIO = 1.2
const MAX_VERTICAL_DRIFT_RATIO = 0.85

type SwipeState = {
  id: number
  startX: number
  startY: number
  startTime: number
  isLocked: boolean
}

export function isEnabledSwipeBack() {
  if (isTMA() || isTMAFullscreen()) return true

  return false
}

function getEdgeWidth(): number {
  if (isTMA() && !isTMADesktop()) return MOBILE_EDGE_WIDTH

  if (typeof window !== 'undefined' && 'ontouchstart' in window) return MOBILE_EDGE_WIDTH

  return DESKTOP_EDGE_WIDTH
}

function getFrameLeft(): number {
  const frame = getPhoneFramePortalElement()

  return frame?.getBoundingClientRect().left ?? 0
}

function getFrameWidth(): number {
  const frame = getPhoneFramePortalElement()

  return frame?.getBoundingClientRect().width ?? window.innerWidth
}

function isNearLeftEdge(clientX: number): boolean {
  return clientX - getFrameLeft() <= getEdgeWidth()
}

function canGoBack(): boolean {
  if (sheetStore.hasOpen()) return true

  const idx = (window.history.state as { idx?: number } | null)?.idx

  return typeof idx === 'number' ? idx > 0 : window.history.length > 1
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false

  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
}

function findTouch(touches: TouchList, id: number): Touch | undefined {
  for (let index = 0; index < touches.length; index += 1) {
    const touch = touches.item(index)

    if (touch?.identifier === id) return touch
  }

  return undefined
}

function shouldTriggerBack(dx: number, absY: number, velocity: number, isLocked: boolean): boolean {
  if (dx <= 0) return false

  if (absY > dx * MAX_VERTICAL_DRIFT_RATIO && absY > 32) return false

  const isHorizontal = dx > absY * HORIZONTAL_DOMINANCE_RATIO
  const isEnoughDistance = dx >= MIN_BACK_DISTANCE
  const isFastEnough = dx >= MIN_FAST_BACK_DISTANCE && velocity >= FAST_SWIPE_VELOCITY
  const isModerateSwipe = dx >= LOCK_DISTANCE * 3 && isHorizontal

  if (!isLocked && !isModerateSwipe) return false

  return isEnoughDistance || isFastEnough || (isLocked && dx >= MIN_FAST_BACK_DISTANCE)
}

function isTouchGesturesMode(): boolean {
  if (typeof window === 'undefined') return false

  if (isTMA()) return !isTMADesktop()

  return 'ontouchstart' in window
}

export function EdgeSwipeBackLayer({ children }: PropsWithChildren) {
  return (
    <div className={styles.layer}>
      <div className={styles.arrow} data-edge-swipe-arrow aria-hidden>
        <svg className={styles.arrowIcon} viewBox="0 0 24 24" fill="none" role="presentation">
          <path
            d="M14.5 6.5L9 12l5.5 5.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className={styles.routeHost}>{children}</div>
    </div>
  )
}

// Касания внутри чужого iframe не всплывают в document кошелька.
export function EdgeSwipeBackCapture() {
  if (!isEnabledSwipeBack()) return null

  return (
    <div
      className={styles.capture}
      onPointerDown={(event) => {
        if (event.pointerType !== 'touch') event.currentTarget.setPointerCapture(event.pointerId)
      }}
      aria-hidden
    />
  )
}

export function EdgeSwipeBack() {
  const haptic = useHaptic()
  const isTouchMode = isTouchGesturesMode()

  // жест «назад» от левого края экрана (touch на TMA/мобиле, pointer на десктопе)
  useEffect(() => {
    let state: SwipeState | null = null

    const reset = (isAnimated = false) => {
      if (state?.isLocked) hideArrowIndicator(isAnimated)
      state = null
    }

    // начало свайпа: только primary touch у левого края
    const startTracking = (id: number, clientX: number, clientY: number, time: number) => {
      if (!isNearLeftEdge(clientX)) return false

      if (!canGoBack()) return false

      state = {
        id,
        startX: clientX,
        startY: clientY,
        startTime: time,
        isLocked: false,
      }

      return true
    }

    // движение: после lock блокируем вертикальный скролл и двигаем стрелку
    const updateTracking = (clientX: number, clientY: number, preventScroll: () => void) => {
      if (!state) return

      const dx = clientX - state.startX
      const dy = clientY - state.startY
      const absX = Math.abs(dx)
      const absY = Math.abs(dy)

      if (dx < -LOCK_DISTANCE) {
        reset(true)
        return
      }

      if (!state.isLocked) {
        if (absY > LOCK_DISTANCE * 2 && absY > absX) {
          reset(false)
          return
        }

        if (dx < LOCK_DISTANCE || dx < absY * HORIZONTAL_DOMINANCE_RATIO) return

        state.isLocked = true
      }

      updateArrowIndicator(dx, getFrameWidth())
      preventScroll()
    }

    // отпускание: goBack при достаточной дистанции или быстром свайпе
    const finishTracking = (clientX: number, clientY: number, time: number) => {
      if (!state) return

      const dx = clientX - state.startX
      const absY = Math.abs(clientY - state.startY)
      const duration = Math.max(time - state.startTime, 1)
      const velocity = dx / duration
      const isLocked = state.isLocked
      const shouldGoBack = shouldTriggerBack(dx, absY, velocity, isLocked)

      state = null
      hideArrowIndicator(false)

      if (shouldGoBack && canGoBack()) {
        haptic('selection')
        navigation.goBack()
      }
    }

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return

      const touch = event.touches[0]

      if (!touch || isEditableTarget(event.target)) return

      startTracking(touch.identifier, touch.clientX, touch.clientY, event.timeStamp)
    }

    const onTouchMove = (event: TouchEvent) => {
      if (!state) return

      const touch = findTouch(event.touches, state.id)

      if (!touch) return

      updateTracking(touch.clientX, touch.clientY, () => event.preventDefault())
    }

    const onTouchEnd = (event: TouchEvent) => {
      if (!state) return

      const touch = findTouch(event.changedTouches, state.id)

      if (!touch) return

      finishTracking(touch.clientX, touch.clientY, event.timeStamp)
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return

      if (!event.isPrimary) return

      if (isEditableTarget(event.target)) return

      startTracking(event.pointerId, event.clientX, event.clientY, event.timeStamp)
    }

    const onPointerMove = (event: PointerEvent) => {
      if (!state || event.pointerId !== state.id) return

      updateTracking(event.clientX, event.clientY, () => event.preventDefault())
    }

    const onPointerUp = (event: PointerEvent) => {
      if (!state || event.pointerId !== state.id) return

      finishTracking(event.clientX, event.clientY, event.timeStamp)
    }

    const captureOptions: AddEventListenerOptions = { capture: true }
    const capturePassiveFalse: AddEventListenerOptions = { capture: true, passive: false }
    const onTouchCancel = () => reset(true)
    const onPointerCancel = () => reset(true)

    if (isTouchMode) {
      document.addEventListener('touchstart', onTouchStart, captureOptions)
      document.addEventListener('touchmove', onTouchMove, capturePassiveFalse)
      document.addEventListener('touchend', onTouchEnd, captureOptions)
      document.addEventListener('touchcancel', onTouchCancel, captureOptions)
    } else {
      document.addEventListener('pointerdown', onPointerDown, captureOptions)
      document.addEventListener('pointermove', onPointerMove, capturePassiveFalse)
      document.addEventListener('pointerup', onPointerUp, captureOptions)
      document.addEventListener('pointercancel', onPointerCancel, captureOptions)
    }

    // снятие слушателей и сброс индикатора при размонтировании
    return () => {
      reset(false)

      if (isTouchMode) {
        document.removeEventListener('touchstart', onTouchStart, captureOptions)
        document.removeEventListener('touchmove', onTouchMove, capturePassiveFalse)
        document.removeEventListener('touchend', onTouchEnd, captureOptions)
        document.removeEventListener('touchcancel', onTouchCancel, captureOptions)
      } else {
        document.removeEventListener('pointerdown', onPointerDown, captureOptions)
        document.removeEventListener('pointermove', onPointerMove, capturePassiveFalse)
        document.removeEventListener('pointerup', onPointerUp, captureOptions)
        document.removeEventListener('pointercancel', onPointerCancel, captureOptions)
      }
    }
  }, [haptic, isTouchMode])

  return null
}
